import React, { useState, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import LoadingScreen from "./components/site/LoadingScreen";
import Index from "./pages/Index";
import FloatingContactButton from "./components/site/FloatingContactButton";
import CookieConsent from "./components/site/CookieConsent";

const NotFound = lazy(() => import("./pages/NotFound"));
const Products = lazy(() => import("./pages/Products"));
const Doors = lazy(() => import("./pages/Products/Doors"));
const Furniture = lazy(() => import("./pages/Products/Furniture"));
const Projects = lazy(() => import("./pages/Projects"));
const Semifabricate = lazy(() => import("./pages/Semifabricate"));
const Production = lazy(() => import("./pages/Production"));
const FindUs = lazy(() => import("./pages/FindUs"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Cookies = lazy(() => import("./pages/legal/Cookies"));
const GDPR = lazy(() => import("./pages/legal/GDPR"));

const queryClient = new QueryClient();

const RoutesWithTransition = () => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="animate-fade-in">
      <Suspense fallback={<div className="min-h-screen" />}>
        <Routes location={location}>
          <Route path="/" element={<Index />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/doors" element={<Doors />} />
          <Route path="/products/furniture" element={<Furniture />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/semifabricate" element={<Semifabricate />} />
          <Route path="/production" element={<Production />} />
          <Route path="/find-us" element={<FindUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/legal/terms" element={<Terms />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/cookies" element={<Cookies />} />
          <Route path="/legal/gdpr" element={<GDPR />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  const handleLoadingComplete = () => {
    setIsLoading(false);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          {isLoading && <LoadingScreen onLoadingComplete={handleLoadingComplete} />}
          <BrowserRouter>
            <div className={`transition-opacity duration-1000 ${isLoading ? 'opacity-0' : 'opacity-100'}`}>
              <RoutesWithTransition />
              <FloatingContactButton />
              <CookieConsent />
            </div>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
};

export default App;
